class CardVariantPicker {
  constructor() {
    this.initializedCards = new WeakSet();
    this.initCards();
    this.observeCards();
  }

  initCards(root = document) {
    root.querySelectorAll?.('.card-wrapper[data-product-id]').forEach((card) => {
      this.initCard(card);
    });

    if (root.matches?.('.card-wrapper[data-product-id]')) this.initCard(root);
  }

  observeCards() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) this.initCards(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  formatMoney(cents, moneyFormat, decimals = 2) {
    if (!moneyFormat) return (cents / 100).toFixed(decimals);

    const value = cents / 100;
    const fixed = value.toFixed(decimals);
    const [intPart, decPart = ''] = fixed.split('.');
    const thousandsDot = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const thousandsSpace = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
    const thousandsApostrophe = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

    const replacements = {
      amount: fixed,
      amount_no_decimals: Math.round(value).toString(),
      amount_with_comma_separator: decPart ? `${thousandsDot},${decPart}` : thousandsDot,
      amount_no_decimals_with_comma_separator: thousandsDot,
      amount_with_space_separator: decPart ? `${thousandsSpace},${decPart}` : thousandsSpace,
      amount_no_decimals_with_space_separator: thousandsSpace,
      amount_with_period_and_space_separator: decPart ? `${thousandsSpace}.${decPart}` : thousandsSpace,
      amount_with_apostrophe_separator: decPart ? `${thousandsApostrophe}.${decPart}` : thousandsApostrophe,
      amount_no_decimals_with_apostrophe_separator: thousandsApostrophe,
    };

    let result = moneyFormat;
    Object.entries(replacements).forEach(([key, replacement]) => {
      result = result.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), replacement);
    });

    return result;
  }

  getCanCount(variant) {
    if (!variant) return 10;

    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    for (const option of options) {
      const value = String(option).toLowerCase();
      if (/\bmg\b|\d\s*mg/.test(value)) continue;

      const cans = value.match(/(\d+)\s*cans?\b/);
      if (cans) return parseInt(cans[1], 10);

      const rolls = value.match(/(\d+)\s*(?:x\s*)?(?:rolls?|packs?)\b/);
      if (rolls) return parseInt(rolls[1], 10) * 10;
    }

    return 10;
  }

  initCard(card) {
    if (this.initializedCards.has(card)) return;

    const variantsScript = card.querySelector('[data-product-variants]');
    if (!variantsScript) return;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return;
    }

    this.initializedCards.add(card);

    const strengthContainer = card.querySelector('.variant-strength');
    const packContainer = card.querySelector('.variant-pack');
    const image = card.querySelector('[data-card-product-image]');
    const yourPriceEl = card.querySelector('[data-card-your-price]');
    const perCanEl = card.querySelector('[data-card-price-per-can]');
    const moneyFormat = card.dataset.moneyFormat || '{{amount}}';

    this.removeHoverImages(card, image);

    const getActiveValue = (container) => {
      const active = container?.querySelector('.variant-badge--active');
      return active?.dataset.optionValue ?? null;
    };

    const getMediaForVariant = (variant) => {
      if (!variant) return null;
      if (variant.featured_media) return variant.featured_media;

      if (variant.option1) {
        const strengthMatch = variants.find(
          (item) => item.option1 === variant.option1 && item.featured_media
        );
        if (strengthMatch?.featured_media) return strengthMatch.featured_media;
      }

      return variants.find((item) => item.featured_media)?.featured_media ?? null;
    };

    const findVariant = (option1, option2) => {
      if (option1 && option2) {
        return variants.find((item) => item.option1 === option1 && item.option2 === option2) ?? null;
      }
      if (option1) {
        return variants.find((item) => item.option1 === option1) ?? null;
      }
      if (option2) {
        return (
          variants.find((item) => item.option2 === option2 || item.option1 === option2) ?? null
        );
      }
      return variants[0] ?? null;
    };

    const updatePrice = (variant) => {
      if (!variant) return;

      if (yourPriceEl && typeof variant.price === 'number') {
        const quantityInput = card.querySelector('.samiQuickBuy-qty-input');
        const quantity = Math.max(parseInt(quantityInput?.value, 10) || 1, 1);
        yourPriceEl.textContent = this.formatMoney(variant.price * quantity, moneyFormat, 2);
      }

      if (perCanEl && typeof variant.price === 'number') {
        const perCanCents = variant.price / this.getCanCount(variant);
        perCanEl.textContent = this.formatMoney(perCanCents, moneyFormat, 2);
      }
    };

    const applyVariant = (variant) => {
      if (!variant) return;

      const media = getMediaForVariant(variant);
      if (media?.src && image) {
        image.src = media.src;
        image.srcset = media.srcset || media.src;
        image.alt = media.alt || image.alt;
        image.style.opacity = '1';
      }

      updatePrice(variant);
    };

    const updateFromSelection = () => {
      const option1 = getActiveValue(strengthContainer);
      const option2 = getActiveValue(packContainer);
      applyVariant(findVariant(option1, option2));
    };

    const bindOptionGroup = (container) => {
      if (!container) return;

      container.querySelectorAll('.variant-badge').forEach((badge) => {
        const activate = (event) => {
          event.preventDefault();
          event.stopPropagation();
          container.querySelectorAll('.variant-badge').forEach((item) => {
            item.classList.remove('variant-badge--active');
          });
          badge.classList.add('variant-badge--active');
          updateFromSelection();
        };

        badge.addEventListener('click', activate);
        badge.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            activate(event);
          }
        });
      });
    };

    bindOptionGroup(strengthContainer);
    bindOptionGroup(packContainer);
    updateFromSelection();
  }

  removeHoverImages(card, primaryImage) {
    if (!primaryImage) return;

    primaryImage.closest('.media')?.querySelectorAll('img').forEach((img) => {
      if (img !== primaryImage) {
        img.remove();
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CardVariantPicker());
} else {
  new CardVariantPicker();
}
